/**
 * Поля объектов режима «Навигация»: название и тип помещения (docs/wayfinding/layers.md §3.7).
 *
 * Оба — ПОЛЯ СХЕМЫ слоя «Объекты», а не своё хранилище рядом: название — штатное поле
 * имени фичи (`Название`, тип `title`), тип помещения — обычный `select`. Поэтому их сразу
 * видят таблица по группе, фильтры, раскраска, подписи на карте и карточка во вьювере.
 *
 * Значение типа — сама подпись («Лестница»), а не код. Подпись читается как есть везде,
 * куда попадает значение: в подписи на карте, в CSV, во вьювере; а список правится в схеме
 * слоя, как у любого `select`, — свой тип человек допишет сам. Логике навигации (маршрут
 * через лестницы и лифты, пиктограммы) хватит сравнения с константами ниже. Цена —
 * переименовать подпись значит перенести значения миграцией.
 */
import type { CustomPropertyDef, LayerStyleConfig, LayerVisualization } from '../types/layer.js';
import { FEATURE_STYLE_COLOR_KEY, FEATURE_STYLE_SIZE_KEY } from '../constants/featureStyleOverrides.js';
import {
  WAYFINDING_OBJECT_NAME_LABEL_PROPERTY,
  isWayfindingWaypoint,
  type WayfindingMapSize,
} from '../types/wayfinding.js';
import {
  DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY,
  DEFAULT_FEATURE_NAME_PROPERTY,
  isNamedTitleProperty,
} from './ensureNameCustomProperty.js';
import { withLabelLayoutDefaults } from './layerLabelOwner.js';
import { resolveVisualizations, type GeometryCategory } from './resolveVisualizations.js';
import { stringifyPropertyValue } from './stringifyPropertyValue.js';
import { renderTemplateTokens, renderedTemplateLabel } from './templateTokens.js';
import {
  WAYFINDING_OBJECT_LABEL_PX,
  WAYFINDING_OBJECT_MARKER_PX,
  readWayfindingLayerMarkerSize,
  readWayfindingNavigation,
  scaleWayfindingMapSize,
} from './wayfindingSignTypes.js';

/** Ключ типа помещения в `properties` объекта и имя поля в схеме слоя. */
export const WAYFINDING_ROOM_TYPE_PROPERTY = 'Тип помещения';

/**
 * Номер объекта — номер помещения по экспликации («101», «А-12»): строка, а не число, в
 * номерах бывают буквы и дроби. Поле схемы слоя «Объекты», как название и тип помещения.
 */
export const WAYFINDING_OBJECT_NUMBER_PROPERTY = 'Номер';

export interface WayfindingRoomTypeGroup {
  title: string;
  types: readonly string[];
}

/**
 * Типы — по тому, что навигации нужно знать о месте, а не по назначению помещения вообще:
 * откуда маршрут начинается, через что проходит, как связаны этажи, куда посетителя вести
 * нельзя и какое место получает стандартную пиктограмму на указателе.
 */
export const WAYFINDING_ROOM_TYPE_GROUPS: readonly WayfindingRoomTypeGroup[] = [
  // Концы маршрута. У эвакуационного выхода своя обязательная разметка — отдельно от входа.
  { title: 'Входы и выходы', types: ['Вход / выход', 'Эвакуационный выход'] },
  // Проходные места: через них ведут, но в них не приходят.
  { title: 'Пути', types: ['Коридор', 'Холл'] },
  // Связи этажей: без них не построить многоэтажный маршрут, а доступный маршрут для
  // маломобильных обходит лестницы и эскалаторы.
  { title: 'Между этажами', types: ['Лестница', 'Лифт', 'Эскалатор', 'Пандус'] },
  // Помещения по доступу: в служебные и технические посетителя не ведут.
  {
    title: 'Помещения',
    types: ['Общего пользования', 'Зал', 'Кабинет / офис', 'Служебное', 'Техническое'],
  },
  // Места со стандартными пиктограммами — то, что ищут чаще всего.
  {
    title: 'Услуги',
    types: [
      'Туалет',
      'Гардероб',
      'Информация / ресепшн',
      'Касса',
      'Кафе / столовая',
      'Магазин',
      'Медпункт',
      'Комната матери и ребёнка',
    ],
  },
];

/** Все типы подряд, в порядке групп. */
export const WAYFINDING_ROOM_TYPES: readonly string[] = WAYFINDING_ROOM_TYPE_GROUPS.flatMap(
  (group) => group.types,
);

/** Поле типа помещения в схеме слоя: `select` с полным списком. */
export function wayfindingRoomTypePropertyDef(): CustomPropertyDef {
  return {
    name: WAYFINDING_ROOM_TYPE_PROPERTY,
    type: 'select',
    options: [...WAYFINDING_ROOM_TYPES],
  };
}

/**
 * Варианты типа помещения по схеме слоя. Поля нет или у него нет вариантов (слой родился
 * до полей, поле переделали в строку) — общий список: схема допишется при первой правке.
 */
export function wayfindingRoomTypeOptions(customProperties: unknown): readonly string[] {
  const defs: unknown[] = Array.isArray(customProperties) ? customProperties : [];
  const def = defs.find((item) => hasName(item, WAYFINDING_ROOM_TYPE_PROPERTY)) as
    | { options?: unknown }
    | undefined;
  const raw: unknown = def?.options;
  const options = Array.isArray(raw)
    ? raw.filter((option): option is string => typeof option === 'string' && option !== '')
    : [];
  return options.length > 0 ? options : WAYFINDING_ROOM_TYPES;
}

/**
 * Название объекта для правой панели.
 *
 * Поле названия слоя, а при штатном ключе (`Название`) — ещё и прежнее `name`: туда клали имя
 * импорт GeoJSON и перенос узлов графа миграцией 0022 (её слой «Объекты» рождается без схемы).
 * Так же читает список слоя (`readFeatureDisplayName`), иначе панель показывала бы пустое
 * поле у объекта, который в списке назван. `fromLegacyName` — значение пришло из `name`:
 * очистка поля обязана снять и его, иначе оно тут же проступило бы обратно.
 */
export function readWayfindingObjectName(
  properties: Record<string, unknown>,
  nameKey: string,
): { value: string; fromLegacyName: boolean } {
  const own = properties[nameKey];
  if (typeof own === 'number') {
    return { value: String(own), fromLegacyName: false };
  }
  if (typeof own === 'string' && own.trim() !== '') {
    return { value: own, fromLegacyName: false };
  }
  const legacy = properties.name;
  if (nameKey === DEFAULT_FEATURE_NAME_PROPERTY && typeof legacy === 'string' && legacy.trim() !== '') {
    return { value: legacy, fromLegacyName: true };
  }
  return { value: '', fromLegacyName: false };
}

/**
 * Шаблон подписи объекта по умолчанию — только название, как подписывались объекты до шаблона.
 * Переменная — имя поля названия; у слоя со своим полем названия она всё равно читает его
 * (`formatWayfindingObjectLabel`).
 */
export const DEFAULT_WAYFINDING_OBJECT_LABEL_TEMPLATE = `{${DEFAULT_FEATURE_NAME_PROPERTY}}`;

/** Шаблон подписи объектов из свободного jsonb настроек карты; пусто — по умолчанию. */
export function readWayfindingObjectLabelTemplate(settings: unknown): string {
  const template = readWayfindingNavigation(settings).objectLabelTemplate;
  return typeof template === 'string' && template.trim() !== ''
    ? template
    : DEFAULT_WAYFINDING_OBJECT_LABEL_TEMPLATE;
}

/** `{Имя поля}` — любое имя без фигурных скобок: поля объектов называют по-русски и с пробелами. */
const OBJECT_LABEL_TOKEN = /\{([^{}]+)\}/g;

/**
 * Подпись объекта по шаблону карты (✏️ 11.10): поля объекта в фигурных скобках, остальное
 * остаётся как написано — «{Номер} {Название}» → «101 Касса».
 *
 * Поле названия читается как в правой панели (`readWayfindingObjectName`, с прежним `name`), и
 * под обоими именами: штатным `{Название}` и своим полем названия слоя — шаблон один на карту, а
 * поле названия у слоёв бывает разное. Остальные поля — значение свойства как есть; поля нет у
 * объекта — пусто: незаполненное поле в свойства не пишется, и отличить его от опечатки нельзя.
 *
 * `''` — все поля шаблона пусты: подпись из одних разделителей ничего бы не называла.
 */
export function formatWayfindingObjectLabel(
  properties: Record<string, unknown> | null | undefined,
  template: string,
  nameKey: string,
): string {
  const props = properties ?? {};
  return renderedTemplateLabel(
    renderTemplateTokens(template, OBJECT_LABEL_TOKEN, ([raw = '']) => {
      const key = raw.trim();
      if (key === nameKey || key === DEFAULT_FEATURE_NAME_PROPERTY) {
        return readWayfindingObjectName(props, nameKey).value.trim();
      }
      // Имя поля как есть, а не подрезанное: у полей из импорта бывают пробелы по краям.
      const value = raw in props ? props[raw] : props[key];
      // Список (выпадающий список Google-таблицы) — через запятую, как в таблице, а не JSON.
      return (Array.isArray(value) ? value.map(stringifyPropertyValue).join(', ') : stringifyPropertyValue(value)).trim();
    }),
  );
}

/**
 * Пункты списка типа: варианты слоя, разложенные по группам общего списка. Свои варианты
 * слоя и выбранное значение, которого среди вариантов уже нет, — группой «Другие» в конце:
 * иначе дописанный в схему тип был бы недоступен, а выбранный не нашёлся бы в списке и
 * выглядел пустым. Пустые группы не показываются.
 */
export function groupWayfindingRoomTypes(
  options: readonly string[],
  current: string | null,
): WayfindingRoomTypeGroup[] {
  const available = new Set(options);
  const known = new Set(WAYFINDING_ROOM_TYPES);
  const others = options.filter((type) => !known.has(type));
  if (current && !available.has(current)) {
    others.push(current);
  }
  return [
    ...WAYFINDING_ROOM_TYPE_GROUPS.map((group) => ({
      title: group.title,
      types: group.types.filter((type) => available.has(type)),
    })),
    { title: 'Другие', types: others },
  ].filter((group) => group.types.length > 0);
}

function hasName(def: unknown, name: string): boolean {
  return !!def && typeof def === 'object' && (def as { name?: unknown }).name === name;
}

/**
 * Схема слоя «Объекты» с полями названия, номера и типа помещения; `null` — все на месте
 * и писать нечего.
 *
 * Схема — свободный jsonb, поэтому чужие записи в ней (даже битые) не выбрасываются:
 * запись схемы целиком стёрла бы их молча. Поле названия встаёт первым — редактор держит
 * `title` в начале схемы. Поле с тем же именем, но другого типа не трогаем: его завёл
 * человек, и второе одноимённое поле развело бы значения по двум колонкам.
 */
export function withWayfindingObjectFields(customProperties: unknown): CustomPropertyDef[] | null {
  const defs: unknown[] = Array.isArray(customProperties) ? customProperties : [];
  // Поле названия — то же, что найдёт `resolveFeatureNameKey`: `title` без имени ничего не
  // называет и своё поле не заменяет.
  const needsName = !defs.some(
    (def) => isNamedTitleProperty(def) || hasName(def, DEFAULT_FEATURE_NAME_PROPERTY),
  );
  const needsNumber = !defs.some((def) => hasName(def, WAYFINDING_OBJECT_NUMBER_PROPERTY));
  const needsRoomType = !defs.some((def) => hasName(def, WAYFINDING_ROOM_TYPE_PROPERTY));
  if (!needsName && !needsNumber && !needsRoomType) {
    return null;
  }
  return [
    ...(needsName ? [{ ...DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY }] : []),
    ...(defs as CustomPropertyDef[]),
    ...(needsNumber ? [{ name: WAYFINDING_OBJECT_NUMBER_PROPERTY, type: 'string' as const }] : []),
    ...(needsRoomType ? [wayfindingRoomTypePropertyDef()] : []),
  ];
}

/**
 * Подпись слоя «Объекты» по умолчанию — НАЗВАНИЕ объекта: `showLabels` не задан — подписи
 * включены, `labelProperty` не задан — читается `fallbackLabelProperty`. Явно выключенные
 * подписи и явно выбранное поле уважаются. Правило чтения, как у указателей
 * (`withWayfindingSignLabelDefaults`): в слой ничего не пишется, панель слоя показывает те же
 * умолчания — зовёт эту же функцию.
 *
 * @param fallbackLabelProperty поле подписи, если своего у слоя нет: по умолчанию вычисленное
 *   название (его подкладывают в данные карты, `withWayfindingObjectNameLabels`). Тайловому
 *   слою нужно поле из базы — вычисленного свойства в тайлах нет.
 */
export function withWayfindingObjectLabelDefaults(
  styleConfig: Record<string, unknown> | null | undefined,
  fallbackLabelProperty: string = WAYFINDING_OBJECT_NAME_LABEL_PROPERTY,
): Record<string, unknown> {
  const base = styleConfig ?? {};
  return {
    ...base,
    showLabels: typeof base.showLabels === 'boolean' ? base.showLabels : true,
    labelProperty:
      typeof base.labelProperty === 'string' && base.labelProperty !== ''
        ? base.labelProperty
        : fallbackLabelProperty,
  };
}

/**
 * Иконки типов помещений (✏️ 13.09): у каждого типа из общего списка — своя пиктограмма.
 * Её видно в списке объектов и внутри точки объекта на карте. Значение — имя иконки набора
 * Tabler (outline); сами контуры — `ROOM_TYPE_ICON_PATHS`: оттуда их рисуют холст карты, SVG
 * списка и выгрузка планов. Свои варианты слоя («Другие») иконки не получают: гадать
 * пиктограмму по произвольной подписи хуже, чем честно оставить точку пустой.
 */
export const WAYFINDING_ROOM_TYPE_ICONS: Readonly<Record<string, string>> = {
  'Вход / выход': 'door-enter',
  'Эвакуационный выход': 'run',
  Коридор: 'walk',
  Холл: 'armchair',
  Лестница: 'stairs',
  Лифт: 'elevator',
  Эскалатор: 'escalator',
  Пандус: 'wheelchair',
  'Общего пользования': 'users',
  Зал: 'presentation',
  'Кабинет / офис': 'briefcase',
  Служебное: 'lock',
  Техническое: 'tool',
  Туалет: 'toilet-paper',
  Гардероб: 'hanger',
  'Информация / ресепшн': 'info-circle',
  Касса: 'cash',
  'Кафе / столовая': 'tools-kitchen-2',
  Магазин: 'shopping-bag',
  Медпункт: 'first-aid-kit',
  'Комната матери и ребёнка': 'baby-carriage',
};

/** Иконка типа помещения по значению поля; `null` — тип не задан или свой. */
export function wayfindingRoomTypeIcon(value: unknown): string | null {
  return typeof value === 'string' ? (WAYFINDING_ROOM_TYPE_ICONS[value] ?? null) : null;
}

/**
 * Иконки типов помещений на карте рисует клиент по имени (резолвер недостающих иконок, как
 * у знаков указателей): белый контур на прозрачном — ложится на точку любого цвета.
 */
export const WAYFINDING_ROOM_ICON_PREFIX = 'wf-room:';

export function wayfindingRoomIconId(icon: string): string {
  return `${WAYFINDING_ROOM_ICON_PREFIX}${icon}`;
}

/** Стабильный id блока иконок типа помещения — по нему его узнают синк и тесты. */
export const OBJECT_ROOM_ICON_VIZ_ID = 'viz-object-room-icon';

/** Иконка внутри точки объекта — доля её диаметра: контуру Tabler нужны поля до края круга. */
export const ROOM_ICON_TO_MARKER = 0.62;

const ALL_GEOMETRIES: ReadonlySet<GeometryCategory> = new Set(['point', 'line', 'polygon']);

/**
 * Стиль слоя «Объекты» для КАРТЫ (правило чтения, в слой ничего не пишется — как у
 * указателей): подпись по умолчанию — название объекта (`withWayfindingObjectLabelDefaults`),
 * точки — размера из настроек карты, внутри точки — иконка типа помещения.
 *
 * Размер по умолчанию задаёт карта (✏️ 13.09): объекты всех слоёв одного плана одного
 * масштаба. Слой может задать свой (✏️ 23.09, `readWayfindingLayerMarkerSize`). Радиус
 * круговых блоков слоя заменяется, а цвет, обводка и прочее остаются, как человек настроил. Иконки — отдельный symbol-блок поверх точек, «символ по
 * параметру» с пустым фолбэком: у объекта без типа (или со своим типом) иконки нет.
 *
 * @param options.fallbackLabelProperty — см. `withWayfindingObjectLabelDefaults`.
 * @param options.objectSize — диаметр точки из `readWayfindingMapSizes`.
 * @param options.labelSize — размер подписи оттуда же; свой размер подписи слоя важнее.
 */
export function applyWayfindingObjectStyle(
  styleConfig: Record<string, unknown> | null | undefined,
  options?: { fallbackLabelProperty?: string; objectSize?: WayfindingMapSize; labelSize?: WayfindingMapSize },
): Record<string, unknown> {
  const base = withWayfindingObjectLabelDefaults(styleConfig, options?.fallbackLabelProperty);
  // Размер подписи карты — умолчание: свой размер подписи слоя (блок или `labelStyle`) важнее.
  const labelDefaults = { 'text-size': options?.labelSize ?? WAYFINDING_OBJECT_LABEL_PX };
  // Свой размер слоя важнее общего размера карты (`readWayfindingLayerMarkerSize`).
  const objectSize = readWayfindingLayerMarkerSize(
    styleConfig,
    options?.objectSize ?? WAYFINDING_OBJECT_MARKER_PX,
  );
  const resolved = resolveVisualizations(base as unknown as LayerStyleConfig, {
    geometries: ALL_GEOMETRIES,
  });
  const radius = scaleWayfindingMapSize(objectSize, 0.5);
  let hasPoints = false;
  const sized = resolved.map((viz) => {
    if (viz.kind !== 'circle') {
      return viz;
    }
    hasPoints ||= viz.enabled;
    return { ...viz, paint: { ...viz.paint, 'circle-radius': radius } };
  });
  if (!hasPoints) {
    // Точки выключены или их нет вовсе — иконке не на чем стоять.
    return withLabelLayoutDefaults({ ...base, visualizations: sized }, labelDefaults);
  }
  const iconMapping: Record<string, string> = {};
  for (const [type, icon] of Object.entries(WAYFINDING_ROOM_TYPE_ICONS)) {
    iconMapping[type] = wayfindingRoomIconId(icon);
  }
  const icons: LayerVisualization = {
    id: OBJECT_ROOM_ICON_VIZ_ID,
    kind: 'symbol',
    enabled: true,
    paint: {},
    layout: {
      // Пустой фолбэк — «без иконки»: объект без типа остаётся просто точкой.
      'icon-image': '',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    iconSizePx: scaleWayfindingMapSize(objectSize, ROOM_ICON_TO_MARKER),
    iconByProperty: WAYFINDING_ROOM_TYPE_PROPERTY,
    iconMapping,
    // Подписи рисует круговой блок: вторая подпись от иконок легла бы поверх первой.
    suppressText: true,
  };
  return withLabelLayoutDefaults({ ...base, visualizations: [...sized, icons] }, labelDefaults);
}

interface ObjectFeatureLike {
  properties?: Record<string, unknown> | null;
}

/**
 * Радиус промежуточной точки графа на карте, px. Меньше точки объекта: это вершина пути,
 * а не место, и на плане она не должна спорить с объектами.
 */
export const WAYFINDING_WAYPOINT_RADIUS_PX = 4;

/**
 * Коллекция без промежуточных точек графа (`isWayfindingWaypoint`) — для всего, что
 * показывает ОБЪЕКТЫ: список, таблица, карта вне режима «Навигация». Точек нет — та же
 * коллекция: потребители сравнивают данные по ссылке.
 */
export function withoutWayfindingWaypoints<C extends { features: readonly ObjectFeatureLike[] }>(
  collection: C,
): C {
  if (!collection.features.some((feature) => isWayfindingWaypoint(feature.properties))) {
    return collection;
  }
  return {
    ...collection,
    features: collection.features.filter((feature) => !isWayfindingWaypoint(feature.properties)),
  };
}

/**
 * Данные карты слоя «Объекты» в режиме «Навигация»: промежуточные точки графа мельче
 * объектов и цвета графа (✏️ 15.09, Паша: в цвете слоя их путали с конечными точками).
 * Размер и цвет подкладываются штатными «своим размером» и «своим цветом» объекта
 * (`FEATURE_STYLE_SIZE_KEY`, `FEATURE_STYLE_COLOR_KEY`) на чтении — синк карты уже сводит их
 * с выделением точки, а своя ветка `case` в paint слоя это сведение сломала бы. В базу
 * ничего не пишется.
 *
 * @param color — цвет графа (`WAYFINDING_GRAPH_HEX` клиента), тот же, что у рёбер.
 */
export function withWayfindingWaypointMarkers<C extends { features: readonly ObjectFeatureLike[] }>(
  collection: C,
  color: string,
): C {
  let changed = false;
  const features = collection.features.map((feature) => {
    if (
      !isWayfindingWaypoint(feature.properties) ||
      (feature.properties?.[FEATURE_STYLE_SIZE_KEY] === WAYFINDING_WAYPOINT_RADIUS_PX &&
        feature.properties?.[FEATURE_STYLE_COLOR_KEY] === color)
    ) {
      return feature;
    }
    changed = true;
    return {
      ...feature,
      properties: {
        ...feature.properties,
        [FEATURE_STYLE_SIZE_KEY]: WAYFINDING_WAYPOINT_RADIUS_PX,
        [FEATURE_STYLE_COLOR_KEY]: color,
      },
    };
  });
  return changed ? { ...collection, features } : collection;
}

/**
 * Данные карты слоя «Объекты» с вычисленной подписью (`WAYFINDING_OBJECT_NAME_LABEL_PROPERTY`):
 * её читает подпись по умолчанию. Подпись — по шаблону карты (`formatWayfindingObjectLabel`),
 * название в нём — как в правой панели (`readWayfindingObjectName`), поэтому объект, названный
 * только прежним `name`, тоже подписан.
 *
 * Ничего не поменялось — возвращается та же коллекция: синк карты сравнивает данные по
 * ссылке, и новая копия на каждый рендер гоняла бы `setData` впустую.
 */
export function withWayfindingObjectNameLabels<C extends { features: readonly ObjectFeatureLike[] }>(
  collection: C,
  nameKey: string,
  template: string,
): C {
  let changed = false;
  const features = collection.features.map((feature) => {
    // Промежуточная точка графа — не объект: шаблон подписи объектов к ней не относится, и
    // шаблон без полей («Касса») иначе подписал бы каждый изгиб пути. Подписана только своим
    // названием, если оно есть, как до шаблона.
    const name = formatWayfindingObjectLabel(
      feature.properties,
      isWayfindingWaypoint(feature.properties) ? DEFAULT_WAYFINDING_OBJECT_LABEL_TEMPLATE : template,
      nameKey,
    );
    if ((feature.properties?.[WAYFINDING_OBJECT_NAME_LABEL_PROPERTY] ?? '') === name) {
      return feature;
    }
    changed = true;
    return {
      ...feature,
      properties: { ...feature.properties, [WAYFINDING_OBJECT_NAME_LABEL_PROPERTY]: name },
    };
  });
  return changed ? { ...collection, features } : collection;
}
